'use client'

import { useState, useCallback } from 'react'
import Drawer from '@/components/ui/Drawer'
import ProjectForm from './ProjectForm'
import { Project } from '@/lib/types'

interface AddProjectDrawerProps {
  isOpen: boolean
  onClose: () => void
  onProjectAdded: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void
  onPreview?: (previewProject: Partial<Project> | null) => void  // For real-time preview
}

export default function AddProjectDrawer({ 
  isOpen, 
  onClose, 
  onProjectAdded, 
  onPreview 
}: AddProjectDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFormChange = useCallback((formData: Partial<Project>) => {
    // Send preview data to parent for real-time ranking
    if (onPreview && formData.title && formData.title.trim()) {
      // Create a complete preview project with defaults for missing fields
      const previewProject: Partial<Project> = {
        ...formData,
        id: 'preview-temp',
        status: formData.status || 'Current',
        importance: formData.importance ?? 2,
        urgency: formData.urgency ?? 2,
        scope: formData.scope ?? 2,
        enjoyment: formData.enjoyment ?? 3
      }
      onPreview(previewProject)
    } else if (onPreview) {
      // Clear preview if title is empty
      onPreview(null)
    }
  }, [onPreview])

  const handleSave = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    setIsSubmitting(true)
    
    try {
      // Clear preview before saving
      if (onPreview) {
        onPreview(null)
      }
      
      // Add the project
      onProjectAdded(projectData)
      
      // Close drawer
      onClose()
    } catch (error) {
      console.error('Error saving project:', error)
      // Handle error (could show toast notification)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    // Clear any preview
    if (onPreview) {
      onPreview(null)
    }
    onClose()
  }

  const handleDrawerClose = () => {
    // Clear preview when drawer closes (handles ESC key, backdrop click, etc.)
    if (onPreview) {
      onPreview(null)
    }
    onClose()
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleDrawerClose}
      title="Add New Project"
      size="lg"
    >
      <div className="space-y-4">
        {/* Instruction text */}
        <div className="rounded-lg bg-primary-50 p-4 dark:bg-primary-900/20">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-primary-600 dark:text-primary-400 text-lg">💡</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-primary-800 dark:text-primary-200">
                See your project rank in real-time!
              </h3>
              <p className="mt-1 text-sm text-primary-700 dark:text-primary-300">
                As you fill out the form, watch how your project moves up and down in the list behind this panel based on your current mood.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <ProjectForm
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={handleFormChange}
          isSubmitting={isSubmitting}
          showWorkedOnSeed
        />
        
        {/* Loading state */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-white/75 dark:bg-gray-800/75 flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-gray-600 dark:text-gray-300">Saving project...</span>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  )
}